import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { DisplayNamesRegion } from '@medable/i18n-core-v4';
import { LocaleContext, Select, Table, useLocalizeMessage } from 'libs.nucleus';
import { getAssignedConsentDocumentIds } from '../mom_adapter/assignment_join';
const STATUS_PRIORITY = {
    'Published': 3,
    'In Review': 2,
    'Draft': 1,
    'Archived': 0,
};
const getConsentFilePriority = ({ status, version }) => [
    STATUS_PRIORITY[status],
    version,
];
// MOM persists site→consent-key associations, not site→specific-version. The dropdown therefore
// surfaces a single representative per templateKey: Published wins over Draft, highest version
// breaks ties, Archived is excluded. Guardrails ensure at most one Published per key.
// If we show a published template, then the consent will be useable by sites right away.
// If we show draft or in review template, then the consent will be useable by sites after the template is published.
export const getTemplateDisplayNames = (consentFiles) => {
    const preferredByTemplateKey = new Map();
    for (const file of consentFiles) {
        if (file.status === 'Archived') {
            continue;
        }
        const currentPreferred = preferredByTemplateKey.get(file.templateKey);
        if (!currentPreferred) {
            preferredByTemplateKey.set(file.templateKey, file);
            continue;
        }
        const [currentStatusPriority, currentVersion] = getConsentFilePriority(currentPreferred);
        const [candidateStatusPriority, candidateVersion] = getConsentFilePriority(file);
        if (candidateStatusPriority > currentStatusPriority ||
            (candidateStatusPriority === currentStatusPriority && candidateVersion > currentVersion)) {
            preferredByTemplateKey.set(file.templateKey, file);
        }
    }
    return Array.from(preferredByTemplateKey.values());
};
const SiteDocumentsSelectCell = ({ environmentSiteId, consentFiles, assignments, onAssignedConsentDocumentsChange, }) => {
    const translate = useLocalizeMessage();
    const templateDisplayNames = useMemo(() => getTemplateDisplayNames(consentFiles), [consentFiles]); // we don't want to display all versions of the same template key so we get the latest version of the highest status.
    const visibleTemplateKeys = useMemo(() => new Set(templateDisplayNames.map((file) => file.templateKey)), [templateDisplayNames]);
    const consentDocumentIdsInScope = useMemo(() => consentFiles.filter((file) => visibleTemplateKeys.has(file.templateKey)).map((file) => file.id), [consentFiles, visibleTemplateKeys]);
    const options = useMemo(() => templateDisplayNames.map((file) => {
        const title = file.title || file.id;
        const prefix = `v${file.version}  •  ${file.status}`;
        return {
            value: file.id,
            label: `${prefix}  •  ${title}`,
        };
    }), [templateDisplayNames]);
    const selectedOptions = useMemo(() => {
        const assignedIds = new Set(getAssignedConsentDocumentIds(assignments, environmentSiteId));
        const assignedTemplateKeys = new Set(consentFiles.filter((file) => assignedIds.has(file.id)).map((file) => file.templateKey));
        const selectedFileIds = new Set(templateDisplayNames.filter((file) => assignedTemplateKeys.has(file.templateKey)).map((file) => file.id));
        return options.filter((option) => selectedFileIds.has(option.value));
    }, [assignments, consentFiles, environmentSiteId, options, templateDisplayNames]);
    const selectionLabel = useMemo(() => {
        if (selectedOptions.length === 0) {
            return undefined;
        }
        if (selectedOptions.length === 1) {
            return selectedOptions[0]?.label;
        }
        return `(${selectedOptions.length}) ${selectedOptions.map((o) => o.label).join(', ')}`;
    }, [selectedOptions]);
    const handleChange = useCallback((selectedOptions) => {
        // Scope every version for every visible template key so removing the one visible option
        // clears all versions of that resource key from the pending assignment set.
        onAssignedConsentDocumentsChange(environmentSiteId, selectedOptions.map((option) => option.value), consentDocumentIdsInScope);
    }, [consentDocumentIdsInScope, environmentSiteId, onAssignedConsentDocumentsChange]);
    const disabled = consentFiles.length === 0;
    const documentsForSiteAriaLabel = translate('Documents for site');
    return (_jsx(Select, { multiple: true, "aria-label": documentsForSiteAriaLabel, options: options, value: selectedOptions, onChange: handleChange, selectionLabel: selectionLabel, placeholder: translate('Select documents'), width: 'full', disabled: disabled, showMultipleSelectionBadges: false, dataTestId: `site-configuration-site-documents-select-${environmentSiteId}` }));
};
export const SitesTable = ({ data, isLoading, consentFiles, assignments, labelNoResults, hideUnassignedRows = false, hideAssignedRows = false, onAssignedConsentDocumentsChange, }) => {
    const translate = useLocalizeMessage();
    const appLocale = useContext(LocaleContext);
    const getCountryDisplayName = useCallback((countryCode) => {
        if (countryCode === undefined || countryCode.trim().length === 0) {
            return '';
        }
        const displayName = new DisplayNamesRegion(appLocale).of(countryCode);
        return displayName !== countryCode ? displayName : '—';
    }, [appLocale]);
    const visibleData = useMemo(() => {
        if (!hideUnassignedRows && !hideAssignedRows) {
            return data;
        }
        const assignedSiteIds = new Set(assignments.map((a) => a.environmentSiteId));
        return data.filter((site) => (hideUnassignedRows ? assignedSiteIds.has(site.id) : !assignedSiteIds.has(site.id)));
    }, [assignments, data, hideAssignedRows, hideUnassignedRows]);
    const columns = useMemo(() => [
        {
            header: translate('Site name'),
            accessorKey: 'name',
            size: 200,
            cell: (context) => context.getValue() ?? '—',
        },
        {
            header: translate('Study site ID'),
            accessorKey: 'number',
            size: 100,
            cell: (context) => context.getValue() ?? '—',
        },
        {
            header: translate('Country'),
            id: 'site-country',
            size: 160,
            cell: (context) => {
                const countryName = getCountryDisplayName(context.row.original.countryCode);
                return countryName.length > 0 ? countryName : '—';
            },
        },
        {
            id: 'site-consent-documents',
            header: translate('ICF template'),
            size: 400,
            cell: (context) => (_jsx(SiteDocumentsSelectCell, { environmentSiteId: context.row.original.id, consentFiles: consentFiles, assignments: assignments, onAssignedConsentDocumentsChange: onAssignedConsentDocumentsChange }, context.row.original.id)),
        },
    ], [assignments, consentFiles, getCountryDisplayName, onAssignedConsentDocumentsChange, translate]);
    return (_jsx("div", { "data-testid": 'site-configuration-sites-table', children: _jsx(Table, { columns: columns, data: visibleData, getRowId: (row) => String(row.id), isLoading: isLoading, labelNoResults: labelNoResults }) }));
};
